import { useFormTemplateKey } from '@commenda/api/form-builder/form-template-keys';
import { useFormsTemplate } from '@commenda/api/form-builder/forms-templates';
import type { DSLFile, FormFieldSourceConfig } from '@commenda/types';
import { useMemo } from 'react';

import { buildFormFieldOptions } from '../../../hooks/useFormFieldOptions';
import { buildComponentMap } from '../../../utils/dsl-traversal';
import type { OwnershipSummaryValue } from './OwnershipSummaryHelper';

export interface EntitySignatory {
  label: string;
  names: string[];
}

export interface PersonPickerField {
  fieldId: string;
  label: string;
  source: FormFieldSourceConfig;
}

interface UseEntitySignatoriesParams {
  entityDetailsKeyId?: string;
  formData?: Record<string, unknown>;
  value: OwnershipSummaryValue;
}

const EMPTY_FORM_DATA: Record<string, unknown> = {};

export class EntitySignatoryHelper {
  static toIds(raw: unknown): string[] {
    if (typeof raw === 'string') return raw ? [raw] : [];
    if (Array.isArray(raw))
      return raw.filter((id): id is string => typeof id === 'string');
    return [];
  }

  static resolve(
    pickers: PersonPickerField[],
    formData: Record<string, unknown>,
    value: OwnershipSummaryValue
  ): Record<string, EntitySignatory[]> {
    const result: Record<string, EntitySignatory[]> = {};
    if (pickers.length === 0) return result;
    const labelsByPicker = pickers.map(
      (picker) =>
        new Map(
          buildFormFieldOptions(picker.source, formData).map((option) => [
            option.value,
            option.label,
          ])
        )
    );
    Object.entries(value.entries ?? {}).forEach(([id, entry]) => {
      if (entry?.owner?.kind !== 'entity' || !entry.details) return;
      const { details } = entry;
      const signatories = pickers.flatMap((picker, index) => {
        const names = EntitySignatoryHelper.toIds(details[picker.fieldId])
          .map((personId) => labelsByPicker[index]?.get(personId))
          .filter((name): name is string => !!name);
        return names.length > 0 ? [{ label: picker.label, names }] : [];
      });
      if (signatories.length > 0) result[id] = signatories;
    });
    return result;
  }
}

/**
 * Resolves, per entity owner, the people picked in its details sub-form (its
 * authorized signatory or representative) to names, using the host form's
 * person fields the picker sources its options from.
 */
export function useEntitySignatories({
  entityDetailsKeyId,
  formData = EMPTY_FORM_DATA,
  value,
}: UseEntitySignatoriesParams): Record<string, EntitySignatory[]> {
  const { key: formKey } = useFormTemplateKey(entityDetailsKeyId ?? null);
  const { template } = useFormsTemplate(formKey?.liveFormId ?? null);

  const pickers = useMemo((): PersonPickerField[] => {
    if (!template?.dsl) return [];
    const components = buildComponentMap(template.dsl as unknown as DSLFile);
    return Object.values(components).flatMap((component) => {
      const field = component as {
        fieldId?: string;
        label?: string;
        dataSource?: { formField?: FormFieldSourceConfig };
      };
      const source = field.dataSource?.formField;
      if (!field.fieldId || !source?.moduleId) return [];
      return [
        {
          fieldId: field.fieldId,
          label: field.label ?? 'Representative',
          source,
        },
      ];
    });
  }, [template?.dsl]);

  return useMemo(
    () => EntitySignatoryHelper.resolve(pickers, formData, value),
    [pickers, formData, value]
  );
}
